import type { NodeDefinition } from '../../../types/node-definition';

/**
 * MCP UI Renderer Node Definition
 * Renders structured UI components in chat using the MCP-UI protocol
 */
const definition: NodeDefinition = {
  type: 'mcp-ui-renderer',
  name: 'MCP UI Renderer',
  category: 'tools',
  description: 'Renders interactive UI components (charts, visualizations, forms) using HTML/CSS/JavaScript in an isolated iframe',
  icon: 'Palette',
  color: 'green',
  version: '1.0.0',
  
  group: ['transform'],
  
  // Mark as sub-node that connects to AI Agent
  isSubNode: true,
  subNodeConfig: {
    category: 'tool',
    requiredFor: ['aiAgent'],
    multipleAllowed: false // Only one UI renderer per agent
  },
  
  defaults: {
    name: 'MCP UI Renderer'
  },
  
  subtitle: 'Render UI in Chat',
  
  credentials: [],
  
  inputs: [],
  
  outputs: [],
  
  // Anchors define the visual connection points
  anchors: {
    input: [
      {
        type: 'subNode',
        subNodeCategory: 'tool',
        label: 'Tool',
        multiple: false
      }
    ]
  },
  
  tags: ['ui', 'visual', 'render', 'mcp', 'display', 'output', 'response', 'interface'],
  
  comingSoon: false,
  
  // Tool-capable configuration for AI Agent
  toolCapable: {
    enabled: true,
    name: 'render_ui',
    description: `Render structured UI components in chat responses. Supports three content types:
1. rawHtml - Inline HTML/CSS/JavaScript (for charts, visualizations)
2. externalUrl - External website URLs
3. remoteDom - Interactive React components

All content is rendered securely in sandboxed iframes.`,
    allowManualDescription: true,
    parameters: {
      contentType: {
        toolParamName: 'content_type',
        description: 'Type of UI content to render: rawHtml (inline HTML), externalUrl (external website), or remoteDom (interactive component)',
        required: true,
        type: 'string',
        enum: ['rawHtml', 'externalUrl', 'remoteDom']
      },
      content: {
        toolParamName: 'content',
        description: 'The actual content to render. For rawHtml: HTML string. For externalUrl: URL string. For remoteDom: JSON object with script and framework properties',
        required: true,
        type: 'string'
      },
      encoding: {
        toolParamName: 'encoding',
        description: 'Content encoding method: text (plain string) or blob (Base64 encoded). Default: text',
        required: false,
        type: 'string',
        enum: ['text', 'blob']
      },
      uri: {
        toolParamName: 'uri',
        description: 'Unique identifier for the UI resource (format: ui://component-name/instance-id). Auto-generated if not provided',
        required: false,
        type: 'string'
      },
      title: {
        toolParamName: 'title',
        description: 'Display title for the UI element',
        required: false,
        type: 'string'
      },
      metadata: {
        toolParamName: 'metadata',
        description: 'Additional metadata as a JSON object (e.g., description, author, created)',
        required: false,
        type: 'object'
      }
    },
    outputMapping: {
      resource: 'MCP-UI resource object ready for rendering in chat',
      uri: 'URI of the created UI resource',
      mimeType: 'MIME type of the resource'
    },
    timeout: 60000 // 60 second timeout for UI rendering (AI needs time to generate complex visualizations)
  }
};

export default definition;
